import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
  Label,
  FormGroup,
  Badge,
} from 'reactstrap';
import Loading from '../../components/Loading';
import { usuariosService } from '../../services/usuariosService';
import { rolesService } from '../../services/rolesService';
import { sucursalesService } from '../../services/sucursalesService';

function UsuarioForm({ usuario, empresaId, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    Nombre: '',
    Correo: '',
    Contrasena: '',
    Activo: true,
  });
  const [rolesDisponibles, setRolesDisponibles] = useState([]);
  const [rolesSeleccionados, setRolesSeleccionados] = useState([]);
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState([]);
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [cambiarContrasena, setCambiarContrasena] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (usuario) {
      setFormData({
        Nombre: usuario.Nombre || usuario.nombre || '',
        Correo: usuario.Correo || usuario.correo || '',
        Contrasena: '',
        Activo: usuario.Activo !== undefined ? usuario.Activo : (usuario.activo !== undefined ? usuario.activo : true),
      });
      setRolesSeleccionados((usuario.Roles || usuario.roles || []).map(r => r.RolID || r.rolID));
      setSucursalesSeleccionadas((usuario.Sucursales || usuario.sucursales || []).map(s => s.SucursalID || s.sucursalID));
      setCambiarContrasena(false);
    }
  }, [usuario]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      
      // Cargar roles
      const rolesResponse = await rolesService.getAll(true);
      if (rolesResponse.success && rolesResponse.data) {
        const rolesData = Array.isArray(rolesResponse.data) ? rolesResponse.data : [];
        setRolesDisponibles(rolesData.map(r => ({
          RolID: r.RolID || r.rolID,
          Nombre: r.Nombre || r.nombre || '',
          Descripcion: r.Descripcion || r.descripcion,
        })));
      }

      // Cargar sucursales
      if (empresaId) {
        const sucursalesResponse = await sucursalesService.getAll(empresaId);
        if (sucursalesResponse.success && sucursalesResponse.data) {
          const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
          setSucursalesDisponibles(sucursalesData.map(s => ({
            SucursalID: s.SucursalID || s.sucursalID,
            Nombre: s.Nombre || s.nombre || '',
          })));
        }
      }
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleRol = (rolID) => {
    setRolesSeleccionados(prev => {
      if (prev.includes(rolID)) {
        return prev.filter(id => id !== rolID);
      } else {
        return [...prev, rolID];
      }
    });
  };

  const toggleSucursal = (sucursalID) => {
    setSucursalesSeleccionadas(prev => {
      if (prev.includes(sucursalID)) {
        return prev.filter(id => id !== sucursalID);
      } else {
        return [...prev, sucursalID];
      }
    });
  };

  const validate = () => {
    if (!formData.Nombre.trim()) {
      setError('El nombre es requerido');
      return false;
    }

    if (!formData.Correo.trim()) {
      setError('El correo es requerido');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.Correo.trim())) {
      setError('El correo no es válido');
      return false;
    }

    if (!usuario && !formData.Contrasena.trim()) {
      setError('La contraseña es requerida');
      return false;
    }

    if (usuario && cambiarContrasena && !formData.Contrasena.trim()) {
      setError('La contraseña es requerida');
      return false;
    }

    if (formData.Contrasena && formData.Contrasena.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      if (usuario) {
        // Actualizar usuario
        const updateData = {
          EmpresaID: empresaId,
          Nombre: formData.Nombre.trim(),
          Correo: formData.Correo.trim(),
          Activo: formData.Activo,
        };

        if (cambiarContrasena && formData.Contrasena) {
          updateData.Contrasena = formData.Contrasena;
        }

        const response = await usuariosService.update(usuario.UsuarioID || usuario.usuarioID, updateData);

        if (response.success) {
          // Asignar roles
          await usuariosService.asignarRoles(usuario.UsuarioID || usuario.usuarioID, rolesSeleccionados);
          
          // Asignar sucursales
          await usuariosService.asignarSucursales(usuario.UsuarioID || usuario.usuarioID, sucursalesSeleccionadas);
          
          onSuccess();
        } else {
          setError(response.Message || response.message || 'Error al actualizar el usuario');
        }
      } else {
        // Crear usuario
        const createData = {
          EmpresaID: empresaId,
          Nombre: formData.Nombre.trim(),
          Correo: formData.Correo.trim(),
          Contrasena: formData.Contrasena,
        };

        const response = await usuariosService.create(createData);

        if (response.success) {
          const nuevoUsuario = response.data;
          const usuarioId = nuevoUsuario.UsuarioID || nuevoUsuario.usuarioID;

          // Asignar roles
          if (rolesSeleccionados.length > 0) {
            await usuariosService.asignarRoles(usuarioId, rolesSeleccionados);
          }
          
          // Asignar sucursales
          if (sucursalesSeleccionadas.length > 0) {
            await usuariosService.asignarSucursales(usuarioId, sucursalesSeleccionadas);
          }
          
          onSuccess();
        } else {
          setError(response.Message || response.message || 'Error al crear el usuario');
        }
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <Loading />;
  }

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                {usuario ? 'Editar Usuario' : 'Nuevo Usuario'}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        name="Nombre"
                        value={formData.Nombre}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Correo Electrónico *</Label>
                      <Input
                        type="email"
                        name="Correo"
                        value={formData.Correo}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      {usuario ? (
                        <>
                          <FormGroup check>
                            <Label check>
                              <Input
                                type="checkbox"
                                checked={cambiarContrasena}
                                onChange={(e) => setCambiarContrasena(e.target.checked)}
                              />
                              {' '}Cambiar contraseña
                            </Label>
                          </FormGroup>
                          {cambiarContrasena && (
                            <Input
                              type="password"
                              name="Contrasena"
                              value={formData.Contrasena}
                              onChange={handleChange}
                              placeholder="Nueva contraseña"
                              minLength={6}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <Label>Contraseña *</Label>
                          <Input
                            type="password"
                            name="Contrasena"
                            value={formData.Contrasena}
                            onChange={handleChange}
                            required
                            minLength={6}
                          />
                        </>
                      )}
                    </FormGroup>
                  </Col>
                  {usuario && (
                    <Col md="6">
                      <FormGroup>
                        <Label>Estado</Label>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              name="Activo"
                              checked={formData.Activo}
                              onChange={handleChange}
                            />
                            {' '}Activo
                          </Label>
                        </FormGroup>
                      </FormGroup>
                    </Col>
                  )}
                </Row>

                <Row>
                  <Col md="6">
                    <Label>Roles</Label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                      {rolesDisponibles.length === 0 ? (
                        <p className="text-muted">No hay roles disponibles</p>
                      ) : (
                        rolesDisponibles.map((rol) => (
                          <FormGroup check key={rol.RolID}>
                            <Label check>
                              <Input
                                type="checkbox"
                                checked={rolesSeleccionados.includes(rol.RolID)}
                                onChange={() => toggleRol(rol.RolID)}
                              />
                              {' '}{rol.Nombre}
                              {rol.Descripcion && (
                                <small className="text-muted d-block ml-4">
                                  {rol.Descripcion}
                                </small>
                              )}
                            </Label>
                          </FormGroup>
                        ))
                      )}
                    </div>
                    {rolesSeleccionados.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          Roles seleccionados: {rolesSeleccionados.length}
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md="6">
                    <Label>Sucursales</Label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                      {sucursalesDisponibles.length === 0 ? (
                        <p className="text-muted">No hay sucursales disponibles</p>
                      ) : (
                        sucursalesDisponibles.map((sucursal) => (
                          <FormGroup check key={sucursal.SucursalID}>
                            <Label check>
                              <Input
                                type="checkbox"
                                checked={sucursalesSeleccionadas.includes(sucursal.SucursalID)}
                                onChange={() => toggleSucursal(sucursal.SucursalID)}
                              />
                              {' '}{sucursal.Nombre}
                            </Label>
                          </FormGroup>
                        ))
                      )}
                    </div>
                    {sucursalesSeleccionadas.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted">
                          Sucursales seleccionadas: {sucursalesSeleccionadas.length}
                        </small>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md="12" className="text-right">
                    <Button color="secondary" onClick={onCancel} className="mr-2">
                      Cancelar
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default UsuarioForm;

